/* eslint-disable */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaExclamationTriangle } from 'react-icons/fa';
import AccessToken from '../components/accessToken/accessToken';
import { endPoints } from '../api/endpoint';
import { Box, Button, Link, Typography, IconButton, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import {
    containerStyle,
    titleStyle,
    tableStyle,
    thStyle,
    thStyleCenter,
    tdStyle,
    paperStyle,
    popupOverlayStyle,
    popupContentStyle,
    popupTitleStyle,
    zoomModalStyle,
    zoomContentStyle,
    zoomImageStyle,
    imageGridStyle,
    imageContainerStyle,
    imageStyle,
    driftedImageStyle,
    imageButtonStyle,
    downloadButtonStyle,
    linkButtonStyle,
    disabledLinkButtonStyle,
    loadingTextStyle,
    okStatusBoxStyle,
    warningStatusBoxStyle,
    classificationScoreBoxStyle,
    statusIconStyle,
    driftedLabelStyle,
    closeButtonStyle,
    paginationContainerStyle,
    zoomControlsStyle,
    downloadLinkStyle,
    noImagesTextStyle,
} from './style';

const TableComponent = ({ driftBaseline, moduleID }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const { getAccessToken, handleTokenError } = AccessToken();
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [zoomIndex, setZoomIndex] = useState(null);

    // Helper: get image URL robustly (supports strings and objects)
    const getImageUrlAt = (idx) => {
        const img = images[idx];
        if (!img) return '';
        if (typeof img === 'string') return img;
        return img.image_url || img.url || img.path || Object.values(img)[0] || '';
    };

    // Keyboard handling for Escape and arrow navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (zoomIndex !== null) {
                    setZoomIndex(null);
                } else if (showPopup) {
                    setShowPopup(false);
                }
            } else if (e.key === 'ArrowRight' && zoomIndex !== null) {
                setZoomIndex((i) => (i + 1) % images.length);
            } else if (e.key === 'ArrowLeft' && zoomIndex !== null) {
                setZoomIndex((i) => (i - 1 + images.length) % images.length);
            }
        };

        if (showPopup || zoomIndex !== null) {
            window.addEventListener('keydown', onKey);
        }
        return () => window.removeEventListener('keydown', onKey);
    }, [showPopup, zoomIndex, images.length]);

    const fetchImages = async (label) => {
        setLoading(true);
        try {
            const accessToken = await getAccessToken();
            const response = await axios.post(
                endPoints.get_image_urls,
                { model_id: moduleID?.model_id },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            const modelType = driftBaseline?.model_type;
            const imgs = response?.data?.data?.data?.images || [];

            if (modelType && typeof modelType === 'string' && modelType.toLowerCase() === 'classification') {
                setImages(imgs.map((obj) => (typeof obj === 'string' ? obj : Object.values(obj)[0])));
            } else {
                const filtered = imgs.filter((img) => {
                    if (!label) return true;
                    try {
                        return img.drift_flags?.[label] === true;
                    } catch {
                        return false;
                    }
                });
                setImages(filtered);
            }

            setSelectedLabel(label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
            setShowPopup(true);
            setCurrentPage(0);
        } catch (error) {
            console.error('Failed to fetch images', error);
            handleTokenError(error, endPoints.get_image_urls, 'post', { model_id: moduleID?.model_id });
        } finally {
            setLoading(false);
        }
    };

    const renderDetectionStatusBox = (score, status) => {
        const percentage = score.toFixed(2);
        if (status === 'OK') {
            return <div style={okStatusBoxStyle}>{percentage}</div>;
        }
        return (
            <div style={warningStatusBoxStyle}>
                <FaExclamationTriangle style={statusIconStyle} />
                {percentage}
            </div>
        );
    };

    const renderClassificationScoreBox = (score) => {
        const percentage = Math.floor(score);
        return (
            <div style={classificationScoreBoxStyle}>
                <FaExclamationTriangle style={statusIconStyle} />
                {percentage}%
            </div>
        );
    };

    const renderTableContent = () => {
        const modelType = driftBaseline?.model_type;
        const modelTypeLower = modelType && typeof modelType === 'string' ? modelType.toLowerCase() : '';

        // Classification case
        if (modelTypeLower === 'classification') {
            if (driftBaseline?.data?.total_labels > 0) {
                return (
                    <Paper elevation={2} sx={paperStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Label</th>
                                    <th style={thStyleCenter}>Avg Score</th>
                                    <th style={thStyle}>Images</th>
                                </tr>
                            </thead>
                            <tbody>
                                {driftBaseline.data.metrics.map((metric, index) => (
                                    <tr key={index}>
                                        <td style={tdStyle}>{metric.label.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}</td>
                                        <td style={tdStyle}>{renderClassificationScoreBox(metric.avg_score)}</td>
                                        <td style={tdStyle}>
                                            <button
                                                type="button"
                                                onClick={() => fetchImages(metric.label)}
                                                disabled={loading}
                                                style={loading ? disabledLinkButtonStyle : linkButtonStyle}
                                            >
                                                {loading && selectedLabel === metric.label ? 'Loading...' : 'Details'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Paper>
                );
            }
            return <p style={loadingTextStyle}>Every label has average score above threshold value.</p>;
        }

        // Detection case
        if (modelTypeLower === 'detection' && driftBaseline?.data?.metrics_type === 'operational_drift') {
            const driftSummary = driftBaseline.data?.drift_summary || {};
            return (
                <Paper elevation={2} sx={paperStyle}>
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Label (Performance Matrix)</th>
                                <th style={thStyleCenter}>Drift Score</th>
                                <th style={thStyle}>Images</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(driftSummary).map(([key, value], index) => (
                                <tr key={index}>
                                    <td style={tdStyle}>{key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}</td>
                                    <td style={tdStyle}>{renderDetectionStatusBox(value.score, value.status)}</td>
                                    <td style={tdStyle}>
                                        <button
                                            type="button"
                                            onClick={() => fetchImages(key)}
                                            disabled={loading}
                                            style={loading ? disabledLinkButtonStyle : linkButtonStyle}
                                        >
                                            {loading && selectedLabel === key ? 'Loading...' : 'Details'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Paper>
            );
        }

        // Fallback: check localStorage retraining_data model_type
        try {
            const retrainingData = JSON.parse(localStorage.getItem('retraining_data'));
            const mType = retrainingData?.model_type;
            if (mType === 'Detection' || mType === 'Classification' || mType === 'classification' || mType === 'detection') {
                return <p style={loadingTextStyle}>No information to display.</p>;
            }
        } catch (e) {}

        return '';
    };

    const getTitle = () => {
        try {
            const retrainingData = JSON.parse(localStorage.getItem('retraining_data'));
            const modelType = retrainingData?.model_type;
            if (modelType === 'Classification' || modelType === 'classification') return 'Drift Metric';
            if (modelType === 'Detection' || modelType === 'detection') return 'Drift Metric';
            return '';
        } catch (e) {
            console.error('Error reading retraining_data from localStorage:', e);
            return '';
        }
    };

    // when thumbnail is clicked: open zoom at the correct absolute index
    const openZoomAt = (absoluteIndex) => {
        setZoomIndex(absoluteIndex);
    };

    return (
        <div style={containerStyle}>
            <div style={titleStyle}>{getTitle()}</div>
            {renderTableContent()}
            {/* Images popup (thumbnails + pagination) */}
            {showPopup && (
                <div
                    style={popupOverlayStyle}
                    role="dialog"
                    aria-modal="true"
                    onClick={() => {
                        setShowPopup(false);
                        setZoomIndex(null);
                    }}
                >
                    <div style={popupContentStyle} onClick={(e) => e.stopPropagation()} role="document">
                        <h3 style={popupTitleStyle}>{selectedLabel} Images</h3>

                        {images.length > 0 ? (
                            <>
                                {/* Image Grid */}
                                <div style={imageGridStyle}>
                                    {images.slice(currentPage * 12, currentPage * 12 + 12).map((img, idx) => {
                                        const absoluteIndex = currentPage * 12 + idx;
                                        const imageUrl = getImageUrlAt(absoluteIndex);
                                        const isObj = typeof images[absoluteIndex] === 'object';
                                        const isDrifted = isObj && images[absoluteIndex]?.is_drifted;

                                        return (
                                            <div key={absoluteIndex} style={imageContainerStyle}>
                                                <button
                                                    type="button"
                                                    onClick={() => openZoomAt(absoluteIndex)}
                                                    style={imageButtonStyle}
                                                    aria-label={`Open image ${absoluteIndex + 1}`}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={`${selectedLabel}-${absoluteIndex}`}
                                                        style={isDrifted ? driftedImageStyle : imageStyle}
                                                    />
                                                </button>

                                                {/* Download Button */}
                                                <a
                                                    href={imageUrl}
                                                    download
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={downloadButtonStyle}
                                                    aria-label="Download image"
                                                >
                                                    ⬇
                                                </a>

                                                {/* Drifted Label */}
                                                {isDrifted && <span style={driftedLabelStyle}>Drifted</span>}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Pagination */}
                                <Box sx={paginationContainerStyle}>
                                    <Button
                                        variant="contained"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => setCurrentPage((prev) => (prev > 0 ? prev - 1 : Math.ceil(images.length / 12) - 1))}
                                    >
                                        Prev
                                    </Button>

                                    <Typography variant="body1">
                                        Page {currentPage + 1} / {Math.ceil(images.length / 12) || 1}
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => setCurrentPage((prev) => (prev < Math.ceil(images.length / 12) - 1 ? prev + 1 : 0))}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <p style={noImagesTextStyle}>No images available for {selectedLabel}.</p>
                        )}
                        <IconButton
                            onClick={() => {
                                setShowPopup(false);
                                setZoomIndex(null);
                            }}
                            size="large"
                            sx={closeButtonStyle}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            )}

            {/* Zoom modal */}
            {zoomIndex !== null && getImageUrlAt(zoomIndex) && (
                <div role="dialog" aria-modal="true" style={zoomModalStyle} onClick={() => setZoomIndex(null)}>
                    <div onClick={(e) => e.stopPropagation()} style={zoomContentStyle}>
                        <img src={getImageUrlAt(zoomIndex)} alt={`Preview ${zoomIndex + 1}`} style={zoomImageStyle} />

                        <Box sx={zoomControlsStyle}>
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setZoomIndex((i) => (i - 1 + images.length) % images.length)}
                            >
                                Prev
                            </Button>
                            <Link href={getImageUrlAt(zoomIndex)} download underline="none" sx={downloadLinkStyle}>
                                <Button variant="contained" startIcon={<DownloadIcon />}>
                                    Download
                                </Button>
                            </Link>
                            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => setZoomIndex((i) => (i + 1) % images.length)}>
                                Next
                            </Button>
                            <Button variant="contained" color="error" endIcon={<CloseIcon />} onClick={() => setZoomIndex(null)}>
                                Close
                            </Button>
                        </Box>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableComponent;
